import React, { useEffect, useMemo, useState } from 'react';
import GaugeChart from 'react-gauge-chart';
import { min } from 'lodash/fp';

import styles from '../Modules.module.scss';
import { useAppSelector } from '../../../../hooks/react-redux';
import { AverageLevel } from '../../../opinions/analytics/charts/average/AverageLevel';
import { getOpinionAnalyticsScope, getOpinionsWithAverage, getTotalOpinionAverage } from '../../../opinions/analytics/store/selectors';
import { scopeFormatter } from '../../../shared/table/arkTableFormatters';
import { MARKERS_HEIGHT } from '../../../shared/carousel/Carousel';

const { red, amber, gold, yellowGreen, mandatory, grey } = styles;

interface MinifiedOpinionAverageChartProps {
    dimensions: {
        height: number;
        width: number;
    };
}

export const MinifiedOpinionAverageChart: React.FC<MinifiedOpinionAverageChartProps> = ({ dimensions }) => {
    const [activePercentage, setActivePercentage] = useState<number>(0);
    const selectedPercentage = useAppSelector(getTotalOpinionAverage);
    const opinionsWithAverage = useAppSelector(getOpinionsWithAverage);
    const scope = useAppSelector(getOpinionAnalyticsScope);

    const totalOpinionsLabel = useMemo(() => opinionsWithAverage.length > 0 ? `Total ${scopeFormatter(scope)} Opinions: ${opinionsWithAverage.length}` : '', [opinionsWithAverage, scope]);

    useEffect(() => {
        if (selectedPercentage !== activePercentage) {
            setActivePercentage(selectedPercentage);
        }
    }, [activePercentage, selectedPercentage]);

    const wrapperStyle = useMemo(() => ({ height: `${dimensions.height - MARKERS_HEIGHT}px`, width: `${dimensions.width}px` }), [dimensions]);
    const gaugeWidthPercentage = useMemo(() => min([(dimensions.height / dimensions.width) * 140, 95]), [dimensions]);

    return (
        <div className={styles.minifiedOpinionAverageChartWrapper} style={wrapperStyle} data-testid='minified-opinions-average-wrapper'>
            <div className={styles.averageChart}>
                <div className={styles.gaugeChartWrapper}>
                    <GaugeChart
                        nrOfLevels={5}
                        colors={[red, amber, gold, yellowGreen, mandatory]}
                        needleColor={grey}
                        needleBaseColor={grey}
                        hideText
                        percent={selectedPercentage}
                        animate={activePercentage !== selectedPercentage}
                        style={{ width: `${gaugeWidthPercentage}%`, height: '100%', margin: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                        arcPadding={0.02}
                        animDelay={0}
                    />
                </div>
                <div className={styles.conclusionWrapper}>
                    <AverageLevel percentage={selectedPercentage} chartIsFullScreen={false} />
                    <div className={styles.totalOpinionsWrapper}>{totalOpinionsLabel}</div>
                </div>
            </div>
            <div className={styles.chartHeader}>
                <div className={styles.chartTitle}>{scopeFormatter(scope)} Opinion Average</div>
            </div>
        </div>
    );
};
